import type { ComprobanteLeido, Sugerencia } from "@/componentes/ordenes/LecturaComprobante"

/** Quién mandó el dinero. Sin pagador, el ingreso queda POR DETERMINAR. */
export type TipoPagador = "CLIENTE" | "PROVEEDOR" | "EMPLEADO"

export const NOMBRE_TIPO: Record<TipoPagador, string> = {
  CLIENTE: "Cliente",
  PROVEEDOR: "Proveedor",
  EMPLEADO: "Empleado",
}

export type Pagador = {
  tipo: TipoPagador
  id: number
  nombre: string
  documento: string | null
}

export type SoporteIngreso = { id: number; nombre_original: string; mime: string; bytes: number }

export type Ingreso = {
  id: number
  codigo: string
  transaccion_id: number
  cuenta_id: number
  divisa: string
  monto: string
  fecha_valor: string
  referencia: string | null
  concepto: string
  /** `null` = por determinar. */
  pagador_tipo: TipoPagador | null
  pagador_id: number | null
  pagador: string | null
  /** Cobrado a CxC (cliente) o devuelto del saldo de la empresa (proveedor). */
  aplicado: string
  /** Anticipo (cliente), reembolso (proveedor) o abono (empleado). */
  resto: string
  anulado: boolean
  motivo_anulacion: string | null
  creado_en: string
  soportes: SoporteIngreso[]
}

export type CxcAbierta = {
  id: number
  concepto: string
  moneda: string
  fecha_vencimiento: string | null
  saldo: string
  /** En la moneda de la CxC. */
  sugerido: string
  /** Bolívares por 1 USD del BCV al día del ingreso, si la CxC es de la otra moneda. */
  tasa_sugerida: string | null
  /** Lo que `sugerido` consume del ingreso, en la moneda del ingreso. */
  consumo_sugerido: string
}

/** Lo abonado a la CxC (en SU moneda) y, si es de la otra moneda, la tasa BS por 1 USD. */
export type Aplicacion = { cxc_id: number; monto: string; tasa?: string }

/** Lo tecleado por cada CxC: el abono y, si hace falta, la tasa. */
export type Reparto = Record<number, { monto: string; tasa: string }>

export type LecturaIngreso = {
  comprobante: ComprobanteLeido & { ordenante: string | null; ordenante_id: string | null }
  se_puede_usar: boolean
  bloqueos: string[]
  avisos: string[]
  cuenta_sugerida: Sugerencia | null
  pagador_sugerido: { tipo: TipoPagador; id: number; nombre: string; motivo: string } | null
}

export type PartidaCuenta = {
  fecha: string
  tipo: string
  referencia: string
  descripcion: string
  divisa: string
  efecto: string
  saldo: string
}

export type CuentaCliente = {
  cliente_id: number
  cliente: string
  saldos: { divisa: string; pendiente: string; anticipo: string; neto: string }[]
  partidas: PartidaCuenta[]
}

export type CuentaEmpleado = {
  persona_id: number
  empleado: string
  saldos: { divisa: string; debe: string }[]
  partidas: PartidaCuenta[]
}

export type Anticipo = {
  ingreso_id: number
  codigo: string
  fecha: string
  divisa: string
  libre: string
}

/** Lo que el `resto` de un ingreso significa según quién lo pagó. */
export function queEsElResto(tipo: TipoPagador | null): string {
  if (tipo === "CLIENTE") return "Anticipo"
  if (tipo === "PROVEEDOR") return "Reembolso"
  if (tipo === "EMPLEADO") return "Abono a su cuenta"
  return "Sin asignar"
}

/** Lo que guardan las CxC: sólo dólares y bolívares (`Moneda` del servidor). */
export function cobraCxc(divisa: string | undefined): boolean {
  return divisa === "USD" || divisa === "BS"
}
